import { fg } from '@atlaskit/platform-feature-flags/fg';

import { isEmbeddedConfluenceInIframe_DO_NOT_USE } from './isEmbeddedConfluenceInIframe';
import { requireModule } from './requireModule';

const AI_PLANNER_EMBEDDED_CONFLUENCE_SOURCE = 'ai-planner';
const PARENT_PRODUCT_MODULE_NAME = 'parentProduct';
export const MAX_SELECTED_TEXT_LENGTH = 20_000;

export type SendToParentProductPayload =
	| {
			contentId: string;
			contentType?: 'page' | 'blogpost' | 'whiteboard';
			eventType: 'embedded-confluence.text-selection.changed';
			selectedText: string;
			selectionLocalIds?: string[];
			sourceTitle?: string;
			sourceUrl?: string;
	  }
	| {
			contentId: string;
			eventType: 'embedded-confluence.text-selection.cleared';
	  };

type ParentProductModule = {
	sendToParentProduct: (payload: SendToParentProductPayload) => void | Promise<void>;
};

type ChangedPayloadContentType = Extract<
	SendToParentProductPayload,
	{ eventType: 'embedded-confluence.text-selection.changed' }
>['contentType'];

type ParseSendToParentProductPayloadResult =
	| {
			payload: SendToParentProductPayload;
			status: 'valid';
	  }
	| {
			status: 'invalid';
	  };

const isRecord = (payload: unknown): payload is Record<string, unknown> =>
	typeof payload === 'object' && payload !== null && !Array.isArray(payload);

const isSupportedEventType = (
	eventType: unknown,
): eventType is SendToParentProductPayload['eventType'] =>
	eventType === 'embedded-confluence.text-selection.changed' ||
	eventType === 'embedded-confluence.text-selection.cleared';

const readRequiredString = (
	payload: Record<string, unknown>,
	key: 'contentId' | 'selectedText',
): string | null => {
	const value = payload[key];
	return typeof value === 'string' && value.trim() ? value : null;
};

const readOptionalString = (
	payload: Record<string, unknown>,
	key: 'sourceTitle' | 'sourceUrl',
): string | 'invalid' | undefined => {
	const value = payload[key];
	if (value === undefined) {
		return undefined;
	}
	if (typeof value !== 'string') {
		return 'invalid';
	}

	const trimmed = value.trim();
	return trimmed ? trimmed : undefined;
};

const readOptionalStringArray = (
	payload: Record<string, unknown>,
): string[] | 'invalid' | undefined => {
	const value = payload.selectionLocalIds;
	if (value === undefined) {
		return undefined;
	}
	if (!Array.isArray(value) || value.some((localId) => typeof localId !== 'string')) {
		return 'invalid';
	}

	return value.filter((localId) => localId.trim());
};

const readOptionalContentType = (
	payload: Record<string, unknown>,
): ChangedPayloadContentType | 'invalid' | undefined => {
	const value = payload.contentType;
	if (value === undefined) {
		return undefined;
	}
	if (value === 'page' || value === 'blogpost' || value === 'whiteboard') {
		return value;
	}

	return 'invalid';
};

export const parseSendToParentProductPayload = (
	payload: unknown,
): ParseSendToParentProductPayloadResult => {
	if (!isRecord(payload)) {
		return { status: 'invalid' };
	}

	if (!isSupportedEventType(payload.eventType)) {
		return { status: 'invalid' };
	}

	const contentId = readRequiredString(payload, 'contentId');
	if (!contentId) {
		return { status: 'invalid' };
	}

	if (payload.eventType === 'embedded-confluence.text-selection.cleared') {
		return {
			payload: {
				eventType: 'embedded-confluence.text-selection.cleared',
				contentId,
			},
			status: 'valid',
		};
	}

	const selectedText = readRequiredString(payload, 'selectedText');
	if (!selectedText) {
		return { status: 'invalid' };
	}

	const selectionLocalIds = readOptionalStringArray(payload);
	if (selectionLocalIds === 'invalid') {
		return { status: 'invalid' };
	}

	const sourceTitle = readOptionalString(payload, 'sourceTitle');
	if (sourceTitle === 'invalid') {
		return { status: 'invalid' };
	}

	const sourceUrl = readOptionalString(payload, 'sourceUrl');
	if (sourceUrl === 'invalid') {
		return { status: 'invalid' };
	}

	const contentType = readOptionalContentType(payload);
	if (contentType === 'invalid') {
		return { status: 'invalid' };
	}

	return {
		payload: {
			eventType: 'embedded-confluence.text-selection.changed',
			contentId,
			selectedText: selectedText.slice(0, MAX_SELECTED_TEXT_LENGTH),
			...(selectionLocalIds?.length ? { selectionLocalIds } : {}),
			...(sourceTitle ? { sourceTitle } : {}),
			...(sourceUrl ? { sourceUrl } : {}),
			...(contentType ? { contentType } : {}),
		},
		status: 'valid',
	};
};

const getEmbeddedConfluenceSource = (): string | undefined => {
	try {
		return new URL(window.location.href).searchParams.get('embeddedConfluenceSource') ?? undefined;
	} catch {
		return undefined;
	}
};

export const sendToParentProductFromEmbeddedConfluence = async (
	payload: unknown,
): Promise<boolean> => {
	const gateEnabled = fg('embedded_confluence_parent_product_sender');
	if (!gateEnabled) {
		return false;
	}

	const isEmbeddedInIframe = isEmbeddedConfluenceInIframe_DO_NOT_USE();
	if (!isEmbeddedInIframe) {
		return false;
	}

	const embeddedConfluenceSource = getEmbeddedConfluenceSource();
	if (embeddedConfluenceSource !== AI_PLANNER_EMBEDDED_CONFLUENCE_SOURCE) {
		return false;
	}

	const parseResult = parseSendToParentProductPayload(payload);
	if (parseResult.status !== 'valid') {
		return false;
	}

	const module = requireModule<ParentProductModule>(PARENT_PRODUCT_MODULE_NAME);

	if (!module?.sendToParentProduct) {
		return false;
	}

	try {
		await module.sendToParentProduct(parseResult.payload);
		return true;
	} catch {
		return false;
	}
};
